import type { LabResultCategoryGroup } from '@liza/fhir-utils';
import { parseISO } from 'date-fns';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useCodeDisplay } from '../../hooks/useCodeDisplay';
import { useLabResultAgeLabel } from '../../hooks/useLabResultAgeLabel';
import { cn } from '../../utils/cn';
import { formatDateOmitCurrentYear } from '../../utils/date';
import { AbnormalFlagLabel } from './AbnormalFlagLabel';
import { labCategorySummaryLines, sharedTextValue } from './labResultCategoryRows';

export type LabResultCategorySummaryProps = {
  category: LabResultCategoryGroup;
  /** Reference "today" for the relative ages; injectable so a story renders the same every time. */
  now?: Date;
  className?: string;
};

/**
 * A collapsed category's condensed reading: every lab's latest value as `abbreviation value`, labs
 * drawn the same day on one line with that line's date and age at the right. An abnormal value is set
 * in the destructive color so a flagged panel is visible without expanding it. Units are left to the
 * expanded rows; the abbreviations carry them implicitly for a clinician scanning a chart.
 */
export const LabResultCategorySummary = ({
  category,
  now = new Date(),
  className,
}: LabResultCategorySummaryProps): React.ReactElement => {
  const { t } = useTranslation('records');
  const { displayForResultCode } = useCodeDisplay();
  const ageLabel = useLabResultAgeLabel();
  const lines = labCategorySummaryLines(category, displayForResultCode, t('unknown'));

  if (lines.length === 0) {
    return (
      <div className={cn('py-0.5 text-sm text-muted-foreground', className)} aria-label={t('noData')}>
        —
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col text-sm', className)}>
      {lines.map((line, index) => {
        const shared = sharedTextValue(line);
        const drawnAt = line.date ? parseISO(line.date) : undefined;
        return (
          <div
            key={line.date ?? index}
            className={cn(
              'grid grid-cols-[minmax(0,1fr)_auto_auto] items-baseline gap-x-4 py-0.5',
              index > 0 && 'border-t border-dashed border-border',
            )}
          >
            <span className="min-w-0">
              {shared ? (
                <span
                  className={cn(shared.abnormal && 'font-semibold text-destructive')}
                  data-testid={`lab-summary-shared-${line.items.map((item) => item.code).join('-')}`}
                >
                  {t('labSummaryAllSameValue', {
                    names: line.items.map((item) => item.name).join(', '),
                    value: shared.value,
                  })}
                  {shared.abnormal && <AbnormalFlagLabel />}
                </span>
              ) : (
                line.items.map((item, itemIndex) => (
                  <Fragment key={item.code}>
                    {itemIndex > 0 && ', '}
                    <span
                      className={cn(item.abnormal && 'font-semibold text-destructive')}
                      data-testid={`lab-summary-${item.code}`}
                    >
                      {item.name} {item.value}
                      {item.abnormal && <AbnormalFlagLabel />}
                    </span>
                  </Fragment>
                ))
              )}
            </span>
            <span className="text-muted-foreground">{line.date ? formatDateOmitCurrentYear(line.date, now) : ''}</span>
            <span className="w-14 text-right text-muted-foreground">{drawnAt ? ageLabel(drawnAt, now) : ''}</span>
          </div>
        );
      })}
    </div>
  );
};
